import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import moment from "moment";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

const COLORS = {
  appRides: { bg: "#4CAF50", label: "App Rides" },
  dispatcherRides: { bg: "#F2994A", label: "Dispatcher Rides" },
  streetRides: { bg: "#2BBBAD", label: "Street Rides" },
};

const TotalTripChart = ({ data, dailyData, onMonthChange, selectedMonths, charttype }) => {
  // const [chartType, setChartType] = useState("bar"); // "bar" or "pie"
  const [chartType, setChartType] = useState(charttype); // "bar" or "pie"

  // ========== BAR CHART ==========
  const barChartData = useMemo(() => {
    const source = dailyData || data;
    if (!source || !source.labels || source.labels.length === 0) return null;

    const formattedLabels = source.labels.map((label) =>
      moment(label).format("DD MMM YYYY")
    );

    return {
      labels: formattedLabels,
      datasets: [
        {
          label: "App Rides",
          data: source.appRides || [],
          backgroundColor: COLORS.appRides.bg,
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.6,
        },
        {
          label: "Dispatcher Rides",
          data: source.dispatcherRides || [],
          backgroundColor: COLORS.dispatcherRides.bg,
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.6,
        },
        {
          label: "Street Rides",
          data: source.streetRides || [],
          backgroundColor: COLORS.streetRides.bg,
          borderRadius: 4,
          barPercentage: 0.7,
          categoryPercentage: 0.6,
        },
      ],
    };
  }, [data, dailyData]);

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#333",
        bodyColor: "#555",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: { size: 13, weight: "600" },
        bodyFont: { size: 12 },
        boxPadding: 6,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: "#888",
          font: { size: 11 },
          maxRotation: 45,
          minRotation: 0,
        },
        border: { color: "#e0e0e0" },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.06)",
          drawBorder: false,
          borderDash: [4, 4],
        },
        ticks: {
          color: "#888",
          font: { size: 11 },
          precision: 0,
        },
        border: { display: false },
      },
    },
  };

  // ========== PIE CHARTS (per month) ==========
  const pieChartsData = useMemo(() => {
    if (!data || !data.labels || data.labels.length === 0) return [];

    return data.labels.map((label, index) => {
      const appVal = data.appRides[index] || 0;
      const dispVal = data.dispatcherRides[index] || 0;
      const streetVal = data.streetRides[index] || 0;
      const total = appVal + dispVal + streetVal;

      return {
        label,
        total,
        values: { dispatcherRides: dispVal, appRides: appVal, streetRides: streetVal },
        chartData: {
          labels: ["Dispatcher Rides", "App Rides", "Street Rides"],
          datasets: [
            {
              data: [dispVal, appVal, streetVal],
              backgroundColor: [
                COLORS.dispatcherRides.bg,
                COLORS.appRides.bg,
                COLORS.streetRides.bg,
              ],
              borderWidth: 2,
              borderColor: "#fff",
              hoverOffset: 10,
              offset: [70, 0, 0],
            },
          ],
        },
      };
    });
  }, [data]);

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#333",
        bodyColor: "#555",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((context.raw / total) * 100).toFixed(1) : 0;
            return ` ${context.label}: ${context.raw} (${pct}%)`;
          },
        },
      },
    },
  };

  const getPercentage = (val, total) => {
    if (total === 0) return "0.0";
    return ((val / total) * 100).toFixed(1);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.title}>Total Trip</h3>

        {/* Legend */}
        <div style={styles.legendContainer}>
          {Object.entries(COLORS).map(([key, value]) => (
            <div key={key} style={styles.legendItem}>
              <span
                style={{
                  ...styles.legendDot,
                  backgroundColor: value.bg,
                }}
              ></span>
              <span style={styles.legendText}>{value.label}</span>
            </div>
          ))}
        </div>

        {/* Chart type toggle */}
        {/* <div style={styles.toggleContainer}>
          <button
            onClick={() => setChartType("bar")}
            style={{
              ...styles.toggleBtn,
              ...(chartType === "bar" ? styles.toggleBtnActive : {}),
            }}
          >
            Bar
          </button>
          <button
            onClick={() => setChartType("pie")}
            style={{
              ...styles.toggleBtn,
              ...(chartType === "pie" ? styles.toggleBtnActive : {}),
            }}
          >
            Pie Chart
          </button>
        </div> */}

        {/* Month filter */}
        <select
          value={selectedMonths}
          onChange={(e) => onMonthChange(Number(e.target.value))}
          style={styles.monthFilter}
        >
          <option value={1}>1 Month</option>
          <option value={3}>3 Months</option>
          {/* <option value={6}>6 Months</option>
          <option value={12}>12 Months</option> */}
        </select>
      </div>

      {/* Chart Content */}
      {chartType === "bar" ? (
        <div style={styles.barWrapper}>
          {barChartData ? (
            <Bar data={barChartData} options={barOptions} />
          ) : (
            <div style={styles.noData}>No trip data available</div>
          )}
        </div>
      ) : (
        <div style={styles.pieContainer}>
          {pieChartsData.length > 0 ? (
            pieChartsData.map((pie, idx) => (
              <div key={idx} style={styles.pieItem}>
                <div style={styles.pieChartWrapper}>
                  <Pie data={pie.chartData} options={pieOptions} />
                </div>
                <div style={styles.pieLegend}>
                  {Object.entries(COLORS).map(([key, value]) => (
                    <div key={key} style={styles.pieLegendItem}>
                      <span
                        style={{
                          ...styles.pieLegendDot,
                          backgroundColor: value.bg,
                        }}
                      ></span>
                      <span style={styles.pieLegendText}>
                        {value.label}: {pie.values[key]} (
                        {getPercentage(pie.values[key], pie.total)}%)
                      </span>
                    </div>
                  ))}
                </div>
                <div style={styles.pieDate}>{pie.label}</div>
              </div>
            ))
          ) : (
            <div style={styles.noData}>No trip data available</div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "24px 28px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    marginTop: 24,
    marginBottom: 24,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#222",
    margin: 0,
  },
  legendContainer: {
    display: "flex",
    gap: 14,
    flexWrap: "wrap",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
  },
  legendText: {
    fontSize: 12,
    color: "#555",
  },
  toggleContainer: {
    display: "flex",
    borderRadius: 20,
    overflow: "hidden",
    border: "1.5px solid #e0e0e0",
    marginLeft: "auto",
  },
  toggleBtn: {
    padding: "6px 16px",
    border: "none",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: "#fff",
    color: "#555",
    transition: "all 0.2s ease",
    outline: "none",
    fontFamily: "inherit",
  },
  toggleBtnActive: {
    backgroundColor: "#333",
    color: "#fff",
  },
  monthFilter: {
    padding: "6px 14px",
    borderRadius: 20,
    border: "1.5px solid #e0e0e0",
    fontSize: 12,
    color: "#555",
    cursor: "pointer",
    outline: "none",
    backgroundColor: "#fff",
    fontFamily: "inherit",
  },
  barWrapper: {
    height: 320,
    position: "relative",
  },
  pieContainer: {
    display: "flex",
    justifyContent: "center",
    gap: 100,
    flexWrap: "wrap",
    padding: "16px 0",
  },
  pieItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 280,
    maxWidth: 320,
  },
  pieChartWrapper: {
    width: 270,
    height: 270,
    position: "relative",
  },
  pieLegend: {
    marginTop: 12,
  },
  pieLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    marginBottom: 3,
  },
  pieLegendDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
    flexShrink: 0,
  },
  pieLegendText: {
    fontSize: 10,
    color: "#555",
    whiteSpace: "nowrap",
  },
  pieDate: {
    marginTop: 8,
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
  },
  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 200,
    color: "#999",
    fontSize: 15,
    width: "100%",
  },
};

export default TotalTripChart;
